import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  asyncScheduler,
  BehaviorSubject,
  filter as rxFilter,
  first,
  firstValueFrom,
  map as rxMap,
  Observable,
  take,
} from 'rxjs';

import {
  DynamicFormExtension,
  DynamicFormHooks,
  DynamicFormsExtensionsService,
} from '@lockton-apps/dynamic-forms';
import { ProposalLocalFacade } from '../../+state/proposal-local.facade';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { ProposalProductDetailsComponent } from '@lockton-apps/shared/ui/proposal-product-details';
import { concatLatestFrom } from '@ngrx/operators';
import { ProposalStepActions } from '@lockton-apps/proposals/action';
import { FormlySelectOption } from '@ngx-formly/core/select/select-options.pipe';
import {
  AdditionalProductsDetailsService,
  ProposalBuilderService,
} from '@lockton-apps/shared/util/proposal-utils';
import {
  AccaClassOfBusinessId,
  CobMeta,
  CobMetaService,
} from '@lockton-apps/shared/util/constants';
import {
  ApplicationConfigStep,
  ApplicationStep,
  ClientTypeType,
  Partners,
} from '@lockton-apps/shared/interfaces';
import { ProposalsStepCustomizerService } from '../proposals-step-customizer.service';
import { ProposalOfflineProductsComponent } from '@lockton-apps/shared/ui/proposal-offline-products';
import { filter, get, uniqBy } from 'lodash';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';

interface PartnerData {
  proposalId: string | undefined;
  allProductIds: string[];
  clientId: number;
  clientType: ClientTypeType;
  clientName: string;
  portalId: Partners;
  configSteps: ApplicationConfigStep[];
  proposalSteps: ApplicationStep[];
  selectedProducts: AccaClassOfBusinessId[];
}

@Injectable()
export class AccaDynamicFormsExtensionsService extends DynamicFormsExtensionsService {
  private readonly selectedOfflineProductIds: BehaviorSubject<any> =
    new BehaviorSubject([]);

  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected readonly partnerData$: Observable<PartnerData>,
    private readonly store: Store,
    private readonly localFacade: ProposalLocalFacade,
    private readonly stepCustomizer: ProposalsStepCustomizerService,
    private readonly proposalBuilder: ProposalBuilderService,
    private readonly additionalProductsDetails: AdditionalProductsDetailsService,
    private readonly cobMetaService: CobMetaService,
    castObservable: CastObservablePipe,
    documentsService: DocumentsService,
    supportingDocumentsService: SupportingDocumentsService,
    feedbackService: FeedbackService
  ) {
    super(
      castObservable,
      documentsService,
      supportingDocumentsService,
      feedbackService
    );
  }

  override readonly extensions: DynamicFormExtension[] = [
    {
      id: 'additionalProducts',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxFilter(Boolean),
        rxMap(({ allProductIds, selectedProducts, portalId }) =>
          uniqBy(
            this.additionalProductsDetails.getDetails<FormlySelectOption>(
              allProductIds,
              selectedProducts,
              portalId,
              ({ id, title, mapTo }) => {
                const mapToMeta: CobMeta | undefined =
                  this.cobMetaService.getClassOfBusinessMeta(mapTo);

                return {
                  value: get(mapToMeta, 'id', id),
                  label: get(mapToMeta, 'title', title),
                };
              }
            ),
            'value'
          )
        )
      ),
    },
    {
      id: 'productDetails',
      prop: 'component',
      value: ProposalProductDetailsComponent,
    },
    {
      id: 'productDetailsInputs',
      prop: 'inputs',
      value: '',
      mappedValue: () => ({
        cobs: this.partnerData$.pipe(
          rxFilter(Boolean),
          rxMap(({ allProductIds, selectedProducts, portalId }) =>
            this.additionalProductsDetails.getDetails<
              AccaClassOfBusinessId,
              AccaClassOfBusinessId
            >(allProductIds, selectedProducts, portalId, (_, cob) => cob)
          )
        ),
      }),
    },
    {
      id: 'offlineProducts',
      prop: 'component',
      value: ProposalOfflineProductsComponent,
    },
    {
      id: 'offlineProductsInputs',
      prop: 'inputs',
      value: '',
      mappedValue: () => ({
        productIds: this.selectedOfflineProductIds.asObservable(),
      }),
    },
  ];

  override readonly hooks: DynamicFormHooks[] = [
    {
      id: 'additionalProducts',
      hookConfig: {
        onInit: (field) => {
          this.selectedOfflineProductIds.next(
            filter(
              field.formControl?.value,
              (id) =>
                this.cobMetaService.getClassOfBusinessMetaById(id)?.offline
            )
          );
        },
        afterViewInit: (field) => {
          asyncScheduler.schedule(() => {
            field.formControl?.valueChanges
              .pipe(
                take(1),
                concatLatestFrom(() => this.partnerData$)
              )
              .subscribe(
                ([
                  addons,
                  {
                    proposalId,
                    configSteps,
                    proposalSteps,
                    clientType,
                    selectedProducts,
                  },
                ]) => {
                  if (!proposalId) return;

                  const { steps, data } = this.proposalBuilder.rebuildSteps(
                    proposalSteps,
                    configSteps,
                    selectedProducts,
                    field.model,
                    addons,
                    this.stepCustomizer.customize,
                    clientType
                  );

                  this.localFacade.setAdditionalProductsIds(addons);
                  this.store.dispatch(
                    ProposalStepActions.additionalProductsSet({
                      proposalId,
                      application: { steps, data, addons },
                    })
                  );
                }
              );
          });
        },
      },
    },
    {
      id: 'firmName',
      hookConfig: {
        onInit: (field) => {
          if (!field.formControl) {
            return;
          }

          field.formControl.setValue(
            get(field.model, 'coverDetails.yourPractice.tradingName')
          );
        },
      },
    },
    {
      id: 'clientName',
      hookConfig: {
        onInit: async (field) => {
          if (!field.formControl) {
            return;
          }

          if (!field.formControl.value) {
            const data: PartnerData = await firstValueFrom(
              this.partnerData$.pipe(first())
            );

            field.formControl.setValue(data.clientName);
          }
        },
      },
    },
  ];
}
